import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AreaSeries,
  CandlestickSeries,
  ColorType,
  createChart,
  createSeriesMarkers,
  HistogramSeries,
  LineType,
  type AreaData,
  type CandlestickData,
  type HistogramData,
  type IChartApi,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type LineData,
  type MouseEventParams,
  type SeriesMarker,
  type SeriesType,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts'
import { ApiError } from '../api/http'
import {
  clearBarCache,
  getTickerBars,
  getTickerBarsWindow,
  type BarInterval,
  type BarPoint,
  type BarRange,
} from '../api/marketData'
import { fetchAllTransactions, fetchTransactionsForAccount } from '../api/trading'
import { useAuthStore } from '../auth/useAuthStore'
import type { Transaction } from '../api/types'
import { fromLocalChartTime, toLocalChartTime } from '../lib/chartTime'
import {
  isOptionSymbol,
  splitRoundTripCycles,
  underlyingOf,
} from '../lib/activeTrades'
import { useAppSymbols } from '../hooks/useAppSymbols'
import { useLiveQuote } from '../hooks/useLiveQuote'
import { addTickListener, type LiveQuoteTick } from '../api/liveQuotes'
import { useLiveQuotesStore } from '../stores/useLiveQuotesStore'
import { useSettingsStore } from '../stores/useSettingsStore'
import { SearchBar } from './SearchBar'
import { IndicatorSettingsModal } from './chart/IndicatorSettingsModal'
import { IndicatorRenderer } from './chart/IndicatorRenderer'
import { ChartRulesDashboard } from './chart/ChartRulesDashboard'
import type { UserIndicatorConfig, UserVolumeIndicator } from '../lib/indicators/types'
import { getDefaultConfig } from '../lib/indicators/defaultPreset'
import { computeIndicators } from '../lib/indicators/engine'
import { previousTradingSessionWindow } from '../lib/marketHours'

const INTERVALS: BarInterval[] = ['1m', '5m', '15m', '1h', '1d']

export type ChartGridLayout = 'single' | '1x2' | '2x1' | '2x2' | '2x3' | '3x3'

// Layout id is rows × cols. `count` is how many chart cells the layout shows.
export const GRID_DIMS: Record<ChartGridLayout, { cols: number; rows: number; count: number }> = {
  single: { cols: 1, rows: 1, count: 1 },
  '1x2': { cols: 2, rows: 1, count: 2 },
  '2x1': { cols: 1, rows: 2, count: 2 },
  '2x2': { cols: 2, rows: 2, count: 4 },
  '2x3': { cols: 3, rows: 2, count: 6 },
  '3x3': { cols: 3, rows: 3, count: 9 },
}

// Lazy-load chunk size in CALENDAR DAYS per interval. Sized so each chunk
// reliably spans weekends, holidays, and overnight gaps — a fixed-ms window
// scaled to `bars-per-chunk` is too small for intraday intervals (a 1m chunk
// of 500 bars is only ~8h, which lands entirely inside an overnight gap and
// returns empty). The server caps any single request at 366 days.
const CHUNK_DAYS: Record<BarInterval, number> = {
  '1s': 1,
  '1m': 5,
  '5m': 21,
  '15m': 60,
  '1h': 180,
  '1d': 365,
  '1w': 365,
}
// How many consecutive empty chunks we'll skip past in a single trigger
// before bailing out (lets the user pan again to retry). Multi-day weekends
// + market holidays can stack — five chunks of 1m bars = ~25 calendar days
// of search depth per trigger, which clears any realistic gap.
const MAX_EMPTY_CHAIN = 5
// Per-trigger cap on total chunk requests (empty + non-empty). Bounds the
// worst-case fetch storm if the view is very wide or many gaps stack.
const MAX_CHUNKS_PER_TRIGGER = 12
// Hard floor for total empty chunks before we latch as exhausted (handles
// the legitimate "symbol has no older data" case without latching on the
// first overnight window).
const EXHAUST_AFTER_EMPTIES = 30
const INTERVAL_MS: Record<BarInterval, number> = {
  '1s': 1_000,
  '1m': 60_000,
  '5m': 5 * 60_000,
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '1d': 24 * 60 * 60_000,
  '1w': 7 * 24 * 60 * 60_000,
}
const MS_PER_DAY = 24 * 60 * 60_000
const INDICATOR_LABEL_X_OFFSET = 14

type ChartType = 'candle' | 'line'

// Parse what the user typed into the interval modal. Accepts bare numbers
// (1, 5, 15, 60, 1440) and the same numbers with an `m`/`h`/`d` suffix.
function parseIntervalInput(raw: string): BarInterval | null {
  const s = raw.trim().toLowerCase()
  if (s === '1' || s === '1m') return '1m'
  if (s === '5' || s === '5m') return '5m'
  if (s === '15' || s === '15m') return '15m'
  if (s === '60' || s === '60m' || s === '1h') return '1h'
  if (s === '1440' || s === '1440m' || s === '1d' || s === '24h') return '1d'
  return null
}

const UP = '#10b981'
const DOWN = '#ef4444'
// Open positions: not yet closed, so no realized P/L to color by.
const NEUTRAL = '#a1a1aa'
// Area-line palette mirrors PortfolioValueChart so the ticker line mode
// reads the same as the portfolio chart.
const UP_FILL_TOP = 'rgba(16,185,129,0.35)'
const DOWN_FILL_TOP = 'rgba(239,68,68,0.35)'
const FILL_BOTTOM = 'rgba(0,0,0,0)'

// Tolerance in ms for matching crosshair position to trade fills. Scales with
// interval so 1m windows don't merge unrelated fills while 1d windows still
// let you hover-match a whole-day bar.
const FILL_TOLERANCE_MS: Record<BarInterval, number> = {
  '1s': 1_000,
  '1m': 60_000,
  '5m': 5 * 60_000,
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '1d': 24 * 60 * 60_000,
  '1w': 7 * 24 * 60 * 60_000,
}

// Pad the bars window slightly so after-hours fills near (but past) the
// closing bar still get fetched and snapped to the last bar.
const FILL_WINDOW_PAD_MS = 6 * 60 * 60_000

interface CyclePoint {
  /** Snapped to the containing bar's time so the dot sits ON the curve. */
  time: UTCTimestamp
  /** Snapped to the containing bar's close for the same reason. */
  value: number
  /** Original fill (one of possibly several collapsed into this bar). */
  tx: Transaction
}

interface DisplayCycle {
  /** Unique key for React/series tracking. */
  key: string
  /** Color applied to markers + band: green=win, red=loss, gray=open. */
  color: string
  /** Sorted ascending by `time`; deduped — at most one point per bar. */
  points: CyclePoint[]
  /**
   * Direction of the opening leg. Bullish = long-equity, long-call, or
   * short-put (you profit if the underlying rises); bearish = short-equity,
   * long-put, or short-call. Drives arrow direction.
   */
  bias: 'bullish' | 'bearish'
  /** Side of the opening leg (BUY for long cycles, SELL for short cycles). */
  openSide: 'BUY' | 'SELL'
  /** Realized P/L summed across all fills in the cycle (USD). */
  realizedPL: number
  /** Sum of |netAmount| across opening-side fills — denominator for P/L%. */
  costBasis: number
  /** True for cycles that haven't returned to flat yet. */
  isOpen: boolean
}

interface OsiParts {
  underlying: string
  yy: number
  mm: number
  dd: number
  type: 'C' | 'P'
  /** Strike in dollars (decoded from the 8-digit thousandths-of-a-dollar field). */
  strike: number
}

/** Parse a canonical OSI option symbol (e.g. `QQQ260529C00735000`). */
function parseOsi(s: string): OsiParts | null {
  const m = /^([A-Z]+)(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/.exec(s)
  if (!m) return null
  return {
    underlying: m[1],
    yy: Number(m[2]),
    mm: Number(m[3]),
    dd: Number(m[4]),
    type: m[5] as 'C' | 'P',
    strike: Number(m[6]) / 1000,
  }
}

/** Render an OSI symbol compactly: `QQQ 5/29 735C`. */
function fmtOsi(s: string): string {
  const p = parseOsi(s)
  if (!p) return s
  const strikeStr =
    p.strike % 1 === 0 ? `${p.strike}` : p.strike.toFixed(2).replace(/\.?0+$/, '')
  return `${p.underlying} ${p.mm}/${p.dd} ${strikeStr}${p.type}`
}

/** Bullish if a long call or short put; bearish for a long put or short call. */
function biasOf(side: 'BUY' | 'SELL', optType: 'C' | 'P'): 'bullish' | 'bearish' {
  if (side === 'BUY') return optType === 'C' ? 'bullish' : 'bearish'
  return optType === 'P' ? 'bullish' : 'bearish'
}

/** Find the index of the bar that contains `timeMs`. Returns -1 if out of range. */
function findBarIndex(timeMs: number, bars: { t: number }[]): number {
  if (bars.length === 0) return -1
  if (timeMs < bars[0].t || timeMs > bars[bars.length - 1].t) return -1
  let lo = 0
  let hi = bars.length - 1
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1
    if (bars[mid].t <= timeMs) lo = mid
    else hi = mid
  }
  return lo
}

function fmtPrice(n: number): string {
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

/** `#RRGGBB` → `rgba(r,g,b,a)`. Returns the input unchanged for non-hex strings. */
function hexToRgba(hex: string, a: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex)
  if (!m) return hex
  const n = parseInt(m[1], 16)
  return `rgba(${(n >> 16) & 0xff}, ${(n >> 8) & 0xff}, ${n & 0xff}, ${a})`
}

interface Props {
  symbol: string
  /**
   * Fill the parent flex slot instead of using a fixed height. The chart's
   * ResizeObserver already tracks the container, so this lets the chart shrink
   * to whatever vertical space is left — e.g. on mobile when the docked trade
   * panel expands beneath it. Desktop keeps its fixed height via lg: classes.
   */
  fill?: boolean
  /**
   * The docked trade panel is expanded. On mobile (max-lg) this collapses the
   * full price/controls header out of flow so the chart claims the freed height,
   * and surfaces a compact price readout overlaid on the chart's top-left.
   * No effect on desktop.
   */
  panelOpen?: boolean
  /**
   * Render as one cell of the multi-chart grid: flex-fill instead of fixed
   * heights, tighter padding, and document-level hotkeys (digit interval
   * modal, Alt+R) only act while the pointer is over this chart so several
   * mounted instances don't all react to one keystroke.
   */
  gridCell?: boolean
  /** Interval the chart starts on (grid cells open at different timeframes). */
  defaultInterval?: BarInterval
  /** Fired when the user changes the interval — lets the parent persist it. */
  onIntervalChange?: (interval: BarInterval) => void
  /**
   * When provided, typing a letter while this chart is the active grid cell
   * opens an in-pane ticker search (mirroring the digit-driven interval modal);
   * picking a symbol fires this so the parent can swap the cell's ticker.
   */
  onSymbolChange?: (symbol: string) => void
  /**
   * This chart is the active cell of a grid. The active cell owns keyboard
   * control (the digit interval modal, Alt+R) so only one of several mounted
   * grid charts reacts to a keystroke. The active outline is drawn by the
   * parent cell wrapper, not here.
   */
  active?: boolean
  /**
   * This chart is already rendered inside a detached popout window. Hides the
   * "detach" toolbar button (no point popping out a popout) — everything else
   * stays so the window has its own search / interval / indicator controls.
   */
  popout?: boolean
}

interface HoveredBar {
  tMs: number
  o: number
  h: number
  l: number
  c: number
}

interface CursorXY {
  x: number
  y: number
}

// Header price block. Subscribes to the live stream itself (local state) so
// per-tick renders are confined to this small component instead of the whole
// chart. When the streamer is online, the latest tick is authoritative for
// the price — overrides `bars[last].c` (which may lag if a tick landed inside
// an already-flushed bucket). The hovered crosshair still wins because that's
// the user's explicit ask.
// Subscribes to the live stream and returns the latest streamed price/time when
// the streamer is online. Shared by the full header and the compact overlay so
// both reflect the same tick without diverging logic.
function useStreamedPrice(symbol: string) {
  const liveStatus = useLiveQuotesStore((s) => s.status)
  // Cleared on symbol change so a stale price from QQQ doesn't briefly show
  // on AAPL.
  const [streamedTick, setStreamedTick] = useState<{ price: number; tsMs: number } | null>(null)
  useEffect(() => {
    setStreamedTick(null)
    if (!symbol) return
    return addTickListener(symbol, (t: LiveQuoteTick) => {
      const price =
        t.last ??
        t.mark ??
        (t.bid != null && t.ask != null ? (t.bid + t.ask) / 2 : undefined)
      if (price == null || !Number.isFinite(price)) return
      setStreamedTick({ price, tsMs: t.tradeTime ?? t.quoteTime ?? Date.now() })
    })
  }, [symbol])

  const streamLivePrice =
    liveStatus === 'online' && streamedTick != null ? streamedTick.price : null
  const streamLiveTs =
    liveStatus === 'online' && streamedTick != null ? streamedTick.tsMs : null
  return { liveStatus, streamLivePrice, streamLiveTs }
}

const ChartHeader = memo(function ChartHeader({
  symbol,
  hasData,
  isLoading,
  first,
  last,
  hovered,
}: {
  symbol: string
  hasData: boolean
  isLoading: boolean
  first: number
  last: number
  hovered: HoveredBar | null
}) {
  const { streamLivePrice } = useStreamedPrice(symbol)
  const displayValue = hovered?.c ?? streamLivePrice ?? last
  const delta = displayValue - first
  const deltaPct = first > 0 ? (delta / first) * 100 : 0
  const isUp = delta >= 0

  return (
    <div>
      {/* Single inline row. Mobile shows price only; the rest appears at lg+. */}
      <div className="flex items-center gap-x-2.5 gap-y-1 flex-wrap">
        <Link
          to={`/instruments/${symbol}`}
          className="font-semibold text-lg sm:text-xl uppercase tracking-wide text-zinc-100 leading-none hover:text-sky-300 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          {symbol}
        </Link>
        <span className="font-mono text-base sm:text-lg text-zinc-400 leading-none">
          {hasData ? fmtPrice(displayValue) : isLoading ? '…' : '—'}
        </span>
        {hasData && first > 0 && (
          <span
            className={`hidden lg:inline font-mono text-xs ${
              isUp ? 'text-emerald-400' : 'text-red-400'
            }`}
          >
            {isUp ? '+' : ''}
            {fmtPrice(delta)} ({isUp ? '+' : ''}
            {deltaPct.toFixed(2)}%)
          </span>
        )}
      </div>
    </div>
  )
})

// Compact price-only readout overlaid on the chart's top-left. Used on mobile
// when the trade panel is expanded and the full header is collapsed out of flow.
const ChartPriceOverlay = memo(function ChartPriceOverlay({
  symbol,
  hasData,
  last,
  className = '',
}: {
  symbol: string
  hasData: boolean
  last: number
  className?: string
}) {
  const { streamLivePrice } = useStreamedPrice(symbol)
  const displayValue = streamLivePrice ?? last
  if (!hasData) return null
  return (
    <div
      className={`pointer-events-none absolute top-2 left-3 z-10 rounded bg-zinc-950/70 px-2 py-1 backdrop-blur-sm ${className}`}
    >
      <span className="font-mono text-sm text-zinc-100 leading-none">
        {fmtPrice(displayValue)}
      </span>
    </div>
  )
})

export function TickerPriceChart({
  symbol,
  fill = false,
  panelOpen = false,
  gridCell = false,
  defaultInterval = '1m',
  onIntervalChange,
  onSymbolChange,
  active = false,
  popout = false,
}: Props) {
  // Pop this chart out into a standalone browser window showing only itself.
  // The new window loads the SPA fresh (sharing the session + vault cookies),
  // matches the /chart-popout route, and reads symbol/interval from the URL.
  // Indicator config rides along automatically because it's DB-synced settings.
  // Reusing the same window name per symbol re-focuses an existing popout
  // instead of stacking duplicates.
  function openPopout() {
    if (!symbol) return
    const url = `${window.location.origin}/chart-popout?symbol=${encodeURIComponent(
      symbol,
    )}&interval=${encodeURIComponent(interval)}`
    window.open(url, `chart-popout-${symbol}`, 'popup,width=1100,height=720')
  }
  const [interval, setIntervalState] = useState<BarInterval>(defaultInterval)
  function commitInterval(iv: BarInterval) {
    setIntervalState(iv)
    onIntervalChange?.(iv)
  }
  const [intervalMenuOpen, setIntervalMenuOpen] = useState(false)
  const intervalMenuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!intervalMenuOpen) return
    function onDown(e: MouseEvent) {
      if (intervalMenuRef.current && !intervalMenuRef.current.contains(e.target as Node)) {
        setIntervalMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [intervalMenuOpen])
  // Daily bars need a multi-day window or the 1D today-only window returns
  // a single bar. Intraday intervals stay on the 1D session window.
  const range: BarRange = interval === '1d' ? '1Y' : '1D'
  const [chartType, setChartType] = useState<ChartType>('candle')
  // When true, render trade overlays (arrows / dots / lifetime bands / P/L
  // chips) and force the price series into a monochrome treatment so the
  // colored trade marks pop. When false, the chart shows only price with
  // its trend-based coloring.
  const [showTrades, setShowTrades] = useState(false)
  // null = closed. Non-null string = TradingView-style "change interval" modal
  // is open, showing what the user has typed so far. Enter commits, Esc closes.
  const [intervalInput, setIntervalInput] = useState<string | null>(null)
  // null = closed. Non-null = the in-pane ticker search is open, seeded with
  // the first letter the user typed. Only used when onSymbolChange is provided.
  const [symbolSearch, setSymbolSearch] = useState<string | null>(null)
  const [maximized, setMaximized] = useState(false)
  // Indicator configuration is synced to the DB via the settings store; we
  // hand-roll the read/write here (instead of useSetting) because we need to
  // run a one-time v1 → v2 migration on the raw blob before consumers see it.
  const CURRENT_VERSION = 2
  const STORAGE_KEY = 'ticker-chart-indicators'
  const SINGLETON_INDICATOR_TYPES = ['vwap', 'orb', 'premarketHighLow', 'prevDayHighLow', 'volume'] as const
  function backfillMissingSingletons(config: UserIndicatorConfig): UserIndicatorConfig {
    const present = new Set(config.indicators.map(i => i.type))
    const defaults = getDefaultConfig('1m').indicators.filter(
      d => SINGLETON_INDICATOR_TYPES.includes(d.type as typeof SINGLETON_INDICATOR_TYPES[number]) && !present.has(d.type),
    )
    if (defaults.length === 0) return config
    return { ...config, indicators: [...config.indicators, ...defaults] }
  }
  const storedIndicatorRaw = useSettingsStore((s) => s.values[STORAGE_KEY])
  const setStoredSetting = useSettingsStore((s) => s.set)
  const indicatorConfig = useMemo<UserIndicatorConfig>(() => {
    if (storedIndicatorRaw == null) return getDefaultConfig('1m')
    try {
      const parsed = JSON.parse(storedIndicatorRaw) as { version?: number; indicators: unknown }
      if (parsed.version === CURRENT_VERSION) {
        return backfillMissingSingletons(parsed as UserIndicatorConfig)
      }
      if (parsed.version == null || parsed.version < 2) {
        const migrated = { ...parsed } as { version?: number; indicators: unknown[] }
        if (Array.isArray(migrated.indicators)) {
          migrated.indicators = migrated.indicators.map((ind: unknown) => {
            if (typeof ind !== 'object' || ind == null) return ind
            const obj = ind as Record<string, unknown>
            if ((obj.type === 'premarketHighLow' || obj.type === 'prevDayHighLow') && typeof obj.color === 'string') {
              return { ...obj, highColor: obj.color, lowColor: obj.color }
            }
            return ind
          })
        }
        migrated.version = CURRENT_VERSION
        return backfillMissingSingletons(migrated as unknown as UserIndicatorConfig)
      }
      return getDefaultConfig('1m')
    } catch {
      return getDefaultConfig('1m')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedIndicatorRaw])
  const [indicatorModalOpen, setIndicatorModalOpen] = useState(false)
  function saveIndicatorConfig(config: UserIndicatorConfig) {
    setStoredSetting(STORAGE_KEY, JSON.stringify(config))
  }

  const queryClient = useQueryClient()
  const [clearingBars, setClearingBars] = useState(false)

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['ticker-bars', symbol, range, interval],
    queryFn: () => getTickerBars(symbol, range, interval),
    enabled: !!symbol,
    staleTime: 5 * 60_000,
    // Keep recently viewed tickers warm well past react-query's 5min default
    // so revisits paint instantly from cache while the mount refetch runs.
    gcTime: 30 * 60_000,
    // Always refetch on mount so navigating away from a ticker and back
    // pulls any bars that materialized while the chart was unmounted. The
    // server cache + 15s live cooldown keep this from hammering upstream.
    refetchOnMount: 'always',
    retry: 1,
  })

  // Auxiliary fetch for PDH/PDL on 1D range. The main `getTickerBars` call
  // with range='1D' only returns today's (or, on weekends, the last weekday's)
  // session, so the indicator engine has no prior-day bars to scan. Fetch the
  // previous trading day's extended session separately and feed it into the
  // indicator context. Only fires when range='1D' and the prevDayHighLow
  // indicator is enabled; other ranges already include yesterday in the main
  // bars window.
  const prevDayEnabled = useMemo(
    () => indicatorConfig.indicators.some(
      (i) => i.type === 'prevDayHighLow' && i.enabled,
    ),
    [indicatorConfig],
  )

  // `bars` is local state instead of `data.bars` so panning left can prepend
  // older chunks without re-fetching the whole window. Seeded from the
  // useQuery result whenever the (symbol, range, interval) key changes; a
  // staleTime-driven background refetch keeps the same key, so it won't
  // clobber any history the user has already panned in.
  const [bars, setBars] = useState<BarPoint[]>([])
  const first = bars[0]?.c ?? 0
  const last = bars[bars.length - 1]?.c ?? 0
  const EMPTY_OUTPUT = { lines: [], fills: [], priceLevels: [], bands: [], labels: [] }

  // Anchor the prev-day window to the displayed session's first bar (today's
  // pre-market open on weekdays, last weekday's open on Sat/Sun) so the
  // "previous trading day" is always the day before what the user is seeing
  // — not wall-clock yesterday, which on a weekend would collide with the
  // displayed Friday session. Falls back to `now` until bars load.
  const prevDayAnchorMs = bars.length > 0 ? bars[0].t : null
  const prevDayWindow = useMemo(
    () => previousTradingSessionWindow(
      prevDayAnchorMs !== null ? new Date(prevDayAnchorMs) : new Date(),
    ),
    [prevDayAnchorMs],
  )
  const { data: prevDayData } = useQuery({
    queryKey: ['prev-day-bars', symbol, prevDayWindow.startMs, prevDayWindow.endMs],
    queryFn: () => getTickerBarsWindow(symbol, prevDayWindow.startMs, prevDayWindow.endMs, '5m'),
    enabled: !!symbol && range === '1D' && prevDayEnabled && prevDayAnchorMs !== null,
    staleTime: 30 * 60_000,
    retry: 1,
  })

  // Compute indicator lines / levels / fills from the active config.
  const indicatorOutput = useMemo(() => {
    if (bars.length === 0) return EMPTY_OUTPUT
    const lastBarMs = bars[bars.length - 1].t
    const intervalMs = INTERVAL_MS[interval]
    const treatLastBarAsClosed = Date.now() > lastBarMs + intervalMs
    const context: import('../lib/indicators/types').IndicatorContext = {
      symbol,
      interval,
      bars,
      timezone: 'America/New_York',
      nowMs: Date.now(),
      treatLastBarAsClosed,
      prevDayBars: prevDayData?.bars,
    }
    return computeIndicators(context, indicatorConfig)
  }, [bars, interval, symbol, indicatorConfig, prevDayData])

  // Live ticks are routed around React state: each tick paints the series
  // directly (series.update) and accumulates into `pendingTicksRef`; a 1s
  // trailing flush folds the accumulated ticks into `bars` so indicators and
  // overlays keep tracking without re-running every bars-keyed memo on every
  // tick. The header price is handled by <ChartHeader>, which subscribes to
  // the stream itself, so this component never renders per tick.
  const pendingTicksRef = useRef<Map<number, BarPoint>>(new Map())
  const tickFlushTimerRef = useRef<number | null>(null)
  // Last bar time (ms) written to the series — by the setData effect or by a
  // direct tick paint. Guards series.update() against out-of-order buckets,
  // which lightweight-charts rejects ("Cannot update oldest data").
  const paintedTimeRef = useRef(0)

  // Lazy-load coordination. `lazyRef.bars` mirrors the `bars` state so the
  // chart's range-change handler (set up once on mount) can read current
  // bars without re-subscribing on every update. `loading` / `lastErrorAt`
  // / `exhausted` are not React state — they only gate the handler.
  const lazyRef = useRef<{
    symbol: string
    interval: BarInterval
    bars: BarPoint[]
    loading: boolean
    lastErrorAt: number
    exhausted: boolean
    // Effective "ask for older than this" cursor in ms. Advances backward
    // whenever a chunk returns empty (overnight gap, weekend, holiday) so the
    // next request skips that gap instead of asking for the same empty window.
    // Null = use bars[0].t.
    searchBeforeMs: number | null
    // Running tally of consecutive empty chunks. Resets on any non-empty
    // response; latches `exhausted` once it crosses EXHAUST_AFTER_EMPTIES.
    consecutiveEmpty: number
  }>({
    symbol: '',
    interval: '1m',
    bars: [],
    loading: false,
    lastErrorAt: 0,
    exhausted: false,
    searchBeforeMs: null,
    consecutiveEmpty: 0,
  })
  // When true, the next setData effect skips fitContent. Prepending older
  // bars during a pan should NOT zoom out — we want the visible range to
  // stay where the user put it.
  const skipFitRef = useRef(false)
  // Tracks the last (symbol, range, interval) we seeded for. A change here
  // is the only thing that resets local bars and clears the in-flight gate.
  const seedKeyRef = useRef('')
  // Only applies the right-side padding once per mount so it doesn't fight
  // live tick updates that re-trigger the setData effect.
  const hasPaddedRef = useRef(false)
  // Tracks the tail the lightweight-charts series currently holds, so the
  // setData effect can decide whether a live-tick change is incremental (one
  // bar appended, or trailing bar mutated) and reach for `series.update()`
  // instead of redrawing the whole series. Null means "next write must be a
  // full setData" — set by chartType swap, symbol/range/interval reseed, and
  // any case where the array semantics changed wholesale.
  const lastWrittenTailRef = useRef<
    { length: number; lastTime: number; lastBar: BarPoint } | null
  >(null)

  useEffect(() => {
    lazyRef.current.bars = bars
  }, [bars])

  // Reset bars + lazy-load state whenever the (symbol, range, interval) key
  // changes. Merging into bars from the wrong symbol would corrupt the chart.
  useEffect(() => {
    const key = `${symbol}|${range}|${interval}`
    if (seedKeyRef.current === key) return
    seedKeyRef.current = key
    setBars([])
    // Force a full setData on the next effect run — the array is wholly
    // different (different symbol/range/interval), so tail-diff logic can't
    // be trusted to detect the change.
    lastWrittenTailRef.current = null
    lazyRef.current.symbol = symbol
    lazyRef.current.interval = interval
    lazyRef.current.loading = false
    lazyRef.current.lastErrorAt = 0
    lazyRef.current.exhausted = false
    lazyRef.current.searchBeforeMs = null
    lazyRef.current.consecutiveEmpty = 0
  }, [symbol, range, interval])

  // Merge query results into local `bars` whenever they arrive — both the
  // initial fetch and any subsequent refetch (staleTime expiry, window focus,
  // remount). Upsert by timestamp so: (1) lazy-loaded older history is
  // preserved, (2) overlapping bars are refreshed with finalized OHLC, and
  // (3) any tail bars that materialized while the chart was unmounted are
  // appended — closing the "missing candles" gap on re-navigation.
  useEffect(() => {
    const fresh = data?.bars
    if (!fresh || fresh.length === 0) return
    setBars((prev) => {
      if (prev.length === 0) return fresh
      const map = new Map<number, BarPoint>()
      for (const b of prev) map.set(b.t, b)
      for (const b of fresh) map.set(b.t, b)
      const merged = Array.from(map.values()).sort((a, b) => a.t - b.t)
      // If anything past the existing tail changed, force a full setData on
      // the next render — incremental-tail diffing assumes append-or-mutate
      // of the trailing bar, not a multi-bar tail insertion.
      const prevLast = prev[prev.length - 1].t
      const freshLast = fresh[fresh.length - 1].t
      if (freshLast > prevLast) lastWrittenTailRef.current = null
      return merged
    })
  }, [data])

  // Wipes the server-side equity_bars cache for this (symbol, interval), then
  // forces the chart to refetch fresh data. Also resets the local in-memory
  // bars (the seed key trick) so any tick-polluted high/low on the trailing
  // bar gets thrown out — without this, refetched data wouldn't reseed because
  // the seed key only fires on symbol/range/interval changes.
  async function handleClearChartCache() {
    if (!symbol) return
    setClearingBars(true)
    try {
      await clearBarCache(symbol, interval)
      seedKeyRef.current = ''
      setBars([])
      lastWrittenTailRef.current = null
      await queryClient.invalidateQueries({ queryKey: ['ticker-bars', symbol] })
      await refetch()
    } catch (err) {
      console.error('clear chart cache failed', err)
    } finally {
      setClearingBars(false)
    }
  }

  // Live ticks: subscribe to the Schwab streamer for this symbol and patch the
  // trailing bar (or append a new one) on each quote update. The streamer is
  // a no-op for users without a Schwab attachment.
  useLiveQuote(symbol ? [symbol] : [])
  useEffect(() => {
    if (!symbol) return
    const bucketMs = INTERVAL_MS[interval] ?? 60_000
    const pending = pendingTicksRef.current

    // Fold accumulated ticks into React state at most once a second. Merging
    // happens against the *latest* prev inside the updater so a query refetch
    // landing between ticks can't be clobbered by stale tick aggregates.
    function flush() {
      tickFlushTimerRef.current = null
      if (pending.size === 0) return
      const patches = Array.from(pending.values()).sort((a, b) => a.t - b.t)
      pending.clear()
      setBars((prev) => {
        if (prev.length === 0) return prev
        let next: BarPoint[] | null = null
        for (const agg of patches) {
          const arr = next ?? prev
          const tail = arr[arr.length - 1]
          if (agg.t < tail.t) continue // historical bucket, ignore
          if (next == null) next = prev.slice()
          if (agg.t === tail.t) {
            next[next.length - 1] = {
              ...tail,
              h: Math.max(tail.h, agg.h),
              l: Math.min(tail.l, agg.l),
              c: agg.c,
            }
          } else {
            next.push({ t: agg.t, o: agg.o, h: agg.h, l: agg.l, c: agg.c, v: 0 })
          }
        }
        if (next == null) return prev
        // Skip fitContent on the flush — the user's pan/zoom would jump.
        skipFitRef.current = true
        return next
      })
    }

    function onTick(t: LiveQuoteTick) {
      const price =
        t.last ??
        t.mark ??
        (t.bid != null && t.ask != null ? (t.bid + t.ask) / 2 : undefined)
      if (price == null || !Number.isFinite(price)) return
      const tsMs = t.tradeTime ?? t.quoteTime ?? Date.now()
      const bucket = Math.floor(tsMs / bucketMs) * bucketMs
      const stateBars = lazyRef.current.bars
      if (stateBars.length === 0) return
      if (bucket < stateBars[stateBars.length - 1].t) return // historical tick

      const agg = pending.get(bucket)
      if (agg) {
        agg.h = Math.max(agg.h, price)
        agg.l = Math.min(agg.l, price)
        agg.c = price
      } else {
        pending.set(bucket, { t: bucket, o: price, h: price, l: price, c: price, v: 0 })
      }

      // Paint the series immediately (no React render) so the trailing
      // candle moves in lockstep with the stream. The state tail holds
      // everything already flushed; the pending aggregate holds everything
      // since — merging the two is the current truth for this bucket.
      const s = seriesRef.current
      if (s && bucket >= paintedTimeRef.current) {
        const patch = pending.get(bucket)!
        const tail = stateBars[stateBars.length - 1]
        const bar =
          bucket === tail.t
            ? {
                t: bucket,
                o: tail.o,
                h: Math.max(tail.h, patch.h),
                l: Math.min(tail.l, patch.l),
                c: patch.c,
              }
            : patch
        const time = toLocalChartTime(bar.t) as unknown as UTCTimestamp
        if (s.seriesType() === 'Candlestick') {
          ;(s as ISeriesApi<'Candlestick'>).update({
            time,
            open: bar.o,
            high: bar.h,
            low: bar.l,
            close: bar.c,
          })
        } else {
          ;(s as ISeriesApi<'Area'>).update({ time, value: bar.c })
        }
        paintedTimeRef.current = bucket
        // React state hasn't seen this bucket yet (flush is on a 1s trailing
        // timer). If the next setData effect fires before that flush — e.g. a
        // useQuery refetch upserting the previous tail, or a lazy-load
        // prepend — its `newLast` would be older than what we just painted,
        // and an incremental update() based on stale `lastWrittenTailRef`
        // would call series.update() going backwards in time (lightweight-
        // charts rejects with "Cannot update oldest data"). Invalidating the
        // tail tracker forces the next effect to do a full setData, which is
        // always safe.
        const tracked = lastWrittenTailRef.current
        if (tracked != null && (time as unknown as number) > tracked.lastTime) {
          lastWrittenTailRef.current = null
        }
      }

      if (tickFlushTimerRef.current == null) {
        tickFlushTimerRef.current = window.setTimeout(flush, 1000)
      }
    }

    const off = addTickListener(symbol, onTick)
    return () => {
      off()
      if (tickFlushTimerRef.current != null) {
        window.clearTimeout(tickFlushTimerRef.current)
        tickFlushTimerRef.current = null
      }
      pending.clear()
    }
  }, [symbol, interval])

  // App-placed fills aligned to the *actual* bars window so fills and bars
  // share a time axis. (The 1D range, for example, snaps to the last completed
  // session server-side, which may not be today — querying "now" would surface
  // fills that don't belong on the chart.)
  const firstBarT = bars[0]?.t
  const lastBarT = bars[bars.length - 1]?.t
  const startIso = firstBarT != null ? new Date(firstBarT - FILL_WINDOW_PAD_MS).toISOString() : null
  const endIso = lastBarT != null ? new Date(lastBarT + FILL_WINDOW_PAD_MS).toISOString() : null

  // In aggregate ("All accounts") view, fan out to every account so trades
  // placed from any account show up on the chart. In single-account view, the
  // existing behavior — pull only the active account's fills — is preserved.
  const scopeAccountId = useAuthStore((s) => s.scopeAccountId)
  const accounts = useAuthStore((s) => s.accounts)
  const isAggregate = scopeAccountId === null && accounts.length > 1

  const singleQuery = useQuery({
    queryKey: ['ticker-fills', symbol, startIso, endIso],
    queryFn: () => {
      if (!startIso || !endIso) return Promise.resolve<Transaction[]>([])
      return fetchAllTransactions({ start: startIso, end: endIso })
    },
    enabled: !!symbol && !isAggregate && startIso != null && endIso != null,
    staleTime: 60_000,
    retry: 0,
  })

  const aggregateIds = useMemo(
    () => (isAggregate ? accounts.map((a) => a.accountId) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAggregate, accounts.map((a) => a.accountId).join(',')],
  )

  const aggregateQueries = useQueries({
    queries: aggregateIds.map((id) => ({
      queryKey: ['ticker-fills', id, symbol, startIso, endIso],
      queryFn: () => {
        if (!startIso || !endIso) return Promise.resolve<Transaction[]>([])
        return fetchTransactionsForAccount(id, { start: startIso, end: endIso })
      },
      enabled: !!symbol && startIso != null && endIso != null,
      staleTime: 60_000,
      retry: 0,
    })),
  })

  const txData = useMemo<Transaction[] | undefined>(() => {
    if (!isAggregate) return singleQuery.data
    if (aggregateQueries.length === 0) return []
    // Wait for every account to land before rendering — otherwise cycles
    // would briefly mispair as later accounts' fills stream in.
    if (aggregateQueries.some((q) => !q.data)) return undefined
    const merged: Transaction[] = []
    for (const q of aggregateQueries) {
      if (q.data) merged.push(...q.data)
    }
    return merged
  }, [
    isAggregate,
    singleQuery.data,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    aggregateQueries.map((q) => q.dataUpdatedAt).join(','),
  ])

  // Which symbols this app has placed orders for. Used to decide whether a
  // transaction was app-placed regardless of the (sometimes-unreliable)
  // source-tagging on the transactions endpoint.
  const { appSymbols } = useAppSymbols()

  const cycles = useMemo<DisplayCycle[]>(() => {
    if (!txData || bars.length === 0) return []
    // Filter to TRADE rows for this underlying that we placed via the app.
    // The `source === 'APP'` tag is best-effort enrichment; the appSymbols set
    // from /app-orders is the authoritative signal.
    const symTrades: Transaction[] = []
    for (const tx of txData) {
      if (tx.kind !== 'TRADE') continue
      if (!tx.side || !tx.symbol) continue
      const isOpt = isOptionSymbol(tx.symbol) || tx.instrumentType === 'OPTION'
      const underlying = isOpt ? underlyingOf(tx.symbol) : tx.symbol
      if (underlying !== symbol) continue
      const isApp =
        tx.source === 'APP' ||
        appSymbols.has(tx.symbol) ||
        (isOpt && appSymbols.has(underlying))
      if (!isApp) continue
      symTrades.push(tx)
    }
    symTrades.sort((a, b) => a.timestamp.localeCompare(b.timestamp))

    // Cycles are per exact symbol — a BUY of QQQ-728C can't be closed by a
    // SELL of QQQ-735P, and option fills shouldn't cycle against equity.
    const bySymbol = new Map<string, Transaction[]>()
    for (const tx of symTrades) {
      const list = bySymbol.get(tx.symbol!) ?? []
      list.push(tx)
      bySymbol.set(tx.symbol!, list)
    }

    const closedGroups: Transaction[][] = []
    const openGroups: Transaction[][] = []
    for (const list of bySymbol.values()) {
      const closed = splitRoundTripCycles(list)
      closedGroups.push(...closed)
      const inClosed = new Set<Transaction>()
      for (const c of closed) for (const t of c) inClosed.add(t)
      const open = list.filter((t) => !inClosed.has(t))
      if (open.length > 0) openGroups.push(open)
    }

    function buildPoints(group: Transaction[]): CyclePoint[] {
      const out: CyclePoint[] = []
      for (const tx of group) {
        const t = Date.parse(tx.timestamp)
        if (!Number.isFinite(t)) continue
        const idx = findBarIndex(t, bars)
        if (idx === -1) continue
        const bar = bars[idx]
        const point: CyclePoint = {
          time: toLocalChartTime(bar.t),
          value: bar.c,
          tx,
        }
        // Collapse multiple fills landing in the same bar to a single dot —
        // line-series setData requires strictly ascending unique times.
        const prev = out[out.length - 1]
        if (prev && prev.time === point.time) continue
        out.push(point)
      }
      return out
    }

    function biasOfCycle(cycle: Transaction[]): {
      bias: 'bullish' | 'bearish'
      openSide: 'BUY' | 'SELL'
    } | null {
      const first = cycle[0]
      if (!first || !first.symbol || !first.side) return null
      const openSide = first.side
      const parsed = parseOsi(first.symbol)
      if (parsed) return { bias: biasOf(openSide, parsed.type), openSide }
      // Equity cycle: BUY opener = long/bullish, SELL opener = short/bearish.
      return { bias: openSide === 'BUY' ? 'bullish' : 'bearish', openSide }
    }

    function costBasisOf(cycle: Transaction[], openSide: 'BUY' | 'SELL'): number {
      let cost = 0
      for (const tx of cycle) {
        if (tx.side === openSide) cost += Math.abs(tx.netAmount ?? 0)
      }
      return cost
    }

    const result: DisplayCycle[] = []
    closedGroups.forEach((cycle, i) => {
      const b = biasOfCycle(cycle)
      if (!b) return
      const realizedPL = cycle.reduce((sum, t) => sum + (t.netAmount ?? 0), 0)
      const points = buildPoints(cycle)
      if (points.length === 0) return
      result.push({
        key: `closed-${i}`,
        color: realizedPL >= 0 ? UP : DOWN,
        points,
        bias: b.bias,
        openSide: b.openSide,
        realizedPL,
        costBasis: costBasisOf(cycle, b.openSide),
        isOpen: false,
      })
    })
    openGroups.forEach((group, i) => {
      const b = biasOfCycle(group)
      if (!b) return
      const realizedPL = group.reduce((sum, t) => sum + (t.netAmount ?? 0), 0)
      const points = buildPoints(group)
      if (points.length === 0) return
      result.push({
        key: `open-${i}`,
        color: NEUTRAL,
        points,
        bias: b.bias,
        openSide: b.openSide,
        realizedPL,
        costBasis: costBasisOf(group, b.openSide),
        isOpen: true,
      })
    })
    return result
  }, [txData, symbol, appSymbols, bars])

  const [hovered, setHovered] = useState<HoveredBar | null>(null)
  const [cursorXY, setCursorXY] = useState<CursorXY | null>(null)

  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<SeriesType> | null>(null)
  const markersPluginRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null)
  // Volume histogram, pinned to the bottom ~20% of the price pane via an
  // overlay price scale. Created once in the chart-init effect (independent of
  // the candle/line series, so toggling chart type doesn't disturb it).
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null)
  // Bumps whenever the chart's visible range or size changes so the HTML
  // overlays (lifetime bands, P/L chips) can recompute their coordinates.
  const [overlayTick, setOverlayTick] = useState(0)

  useEffect(() => {
    if (!containerRef.current) return
    const chart = createChart(containerRef.current, {
      layout: {
        // Transparent so the chart inherits the page's bg-zinc-950 instead
        // of fighting it with its own (very close but not identical) solid.
        background: { type: ColorType.Solid, color: 'rgba(0,0,0,0)' },
        textColor: '#a1a1aa',
        fontSize: 11,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: 'rgba(63,63,70,0.25)' },
        horzLines: { color: 'rgba(63,63,70,0.25)' },
      },
      rightPriceScale: {
        borderVisible: false,
      },
      leftPriceScale: { visible: false },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
        // Allow panning past both data edges so the lazy-loader has time to
        // fetch older chunks before the user "hits the wall" on the left.
        fixLeftEdge: false,
        fixRightEdge: false,
      },
      crosshair: { mode: 0 },
      handleScroll: { mouseWheel: true, pressedMouseMove: true },
      handleScale: {
        mouseWheel: true,
        pinch: true,
        axisPressedMouseMove: true,
        axisDoubleClickReset: true,
      },
    })
    chartRef.current = chart

    // Volume histogram on its own overlay scale (priceScaleId ''), squeezed
    // into the bottom 20% so the candles float above it. Created before the
    // price series (added later in the chartType effect) so candles draw on
    // top where they overlap. Per-bar colors are set on each setData; the base
    // color seeds v5's hit-test getters so a hover before first data can't
    // throw. Data is populated by the volume effect below, gated on the toggle.
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceScaleId: '',
      priceFormat: { type: 'volume' },
      color: 'rgba(120,120,130,0.5)',
      priceLineVisible: false,
      lastValueVisible: false,
    })
    volumeSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    })
    volumeSeriesRef.current = volumeSeries

    chart.subscribeCrosshairMove((param: MouseEventParams) => {
      const s = seriesRef.current
      if (!s) return
      if (param.time == null || !param.point) {
        setHovered(null)
        setCursorXY(null)
        return
      }
      setCursorXY({ x: param.point.x, y: param.point.y })
      const point = param.seriesData.get(s) as
        | CandlestickData
        | LineData
        | AreaData
        | undefined
      if (!point) {
        setHovered(null)
        return
      }
      const tSecs = typeof param.time === 'number' ? param.time : 0
      const tMs = fromLocalChartTime(tSecs)
      if ('close' in point) {
        setHovered({ tMs, o: point.open, h: point.high, l: point.low, c: point.close })
      } else {
        const v = point.value
        setHovered({ tMs, o: v, h: v, l: v, c: v })
      }
    })

    const el = containerRef.current
    chart.resize(el.clientWidth, el.clientHeight)
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const { width, height } = entry.contentRect
      if (width > 0 && height > 0) {
        chart.resize(width, height)
        setOverlayTick((t) => t + 1)
      }
    })
    ro.observe(el)

    // Lazy-load older bars when the visible window approaches the leftmost
    // loaded bar. Triggered from the same range-change subscription as the
    // overlay redraw.
    //
    // Why time-based (and not the logical-index threshold we originally
    // tried): lightweight-charts clamps drag-pan at the leftmost bar, so
    // `logical.from` never falls below ~0 in practice. We instead measure
    // "how much room is left to the left edge in chart-time" and trigger
    // BEFORE the user hits the clamp so the next chunk lands first.
    //
    // Fill-the-viewport loop: keep fetching older chunks until the visible
    // window is covered (oldest loaded bar sits past visibleFrom by the 25%
    // buffer), bounded by MAX_CHUNKS_PER_TRIGGER. Empty chunks (overnight,
    // weekend, holiday gaps) advance the cursor without counting against
    // progress, so a single pan can punch through closed-market windows
    // and land on real data even far back. Only after EXHAUST_AFTER_EMPTIES
    // consecutive empties across all triggers do we conclude the symbol
    // has no older data.
    async function maybeLoadOlder() {
      const r = lazyRef.current
      if (r.loading || r.exhausted) return
      if (r.bars.length === 0) return
      if (r.lastErrorAt && Date.now() - r.lastErrorAt < 1000) return
      const visible = chart.timeScale().getVisibleRange()
      if (!visible) return
      const visibleFrom = visible.from as unknown as number
      const visibleTo = visible.to as unknown as number
      const span = visibleTo - visibleFrom
      if (span <= 0) return
      const threshold = span * 0.25
      // Already filled? cursor's chart-time being more than `threshold` past
      // visibleFrom means the next request would land entirely outside the
      // visible window — stop before we start.
      const initialCursor = r.searchBeforeMs ?? r.bars[0].t
      const initialCursorChartT = toLocalChartTime(initialCursor) as unknown as number
      if (visibleFrom - initialCursorChartT > threshold) return

      const chunkMs = (CHUNK_DAYS[r.interval] ?? 5) * MS_PER_DAY
      const reqSymbol = r.symbol
      const reqInterval = r.interval

      r.loading = true
      try {
        let chunksThisTrigger = 0
        let emptiesThisTrigger = 0
        let cursor = initialCursor
        while (
          chunksThisTrigger < MAX_CHUNKS_PER_TRIGGER &&
          emptiesThisTrigger < MAX_EMPTY_CHAIN
        ) {
          // Stop as soon as the cursor has moved past the left-edge buffer.
          const cursorChartT = toLocalChartTime(cursor) as unknown as number
          if (visibleFrom - cursorChartT > threshold) break

          chunksThisTrigger++
          const newEnd = cursor - 1
          const newStart = cursor - chunkMs
          const result = await getTickerBarsWindow(
            reqSymbol,
            newStart,
            newEnd,
            reqInterval,
          )
          // Drop the result if the user switched symbol/interval mid-flight.
          if (
            lazyRef.current.symbol !== reqSymbol ||
            lazyRef.current.interval !== reqInterval
          ) {
            return
          }
          r.lastErrorAt = 0
          const older = result.bars ?? []
          if (older.length > 0) {
            emptiesThisTrigger = 0
            r.consecutiveEmpty = 0
            r.searchBeforeMs = older[0].t
            cursor = older[0].t
            skipFitRef.current = true
            setBars((prev) => [...older, ...prev])
            continue
          }
          // Empty window — almost certainly a closed market gap. Advance
          // the cursor and try the next older chunk in the same trigger.
          emptiesThisTrigger++
          r.consecutiveEmpty++
          cursor = newStart
          r.searchBeforeMs = cursor
          if (r.consecutiveEmpty >= EXHAUST_AFTER_EMPTIES) {
            r.exhausted = true
            return
          }
        }
        // Hit a per-trigger cap. Leave `searchBeforeMs` advanced so the
        // next pan resumes where we stopped rather than re-asking the same
        // windows.
      } catch {
        // Backoff prevents the next range-change event from re-firing the
        // same failed request immediately.
        r.lastErrorAt = Date.now()
      } finally {
        r.loading = false
      }
    }

    // Pan/zoom shifts time→pixel mapping; overlays must reposition.
    const onRange = () => {
      setOverlayTick((t) => t + 1)
      void maybeLoadOlder()
    }
    chart.timeScale().subscribeVisibleTimeRangeChange(onRange)
    chart.timeScale().subscribeVisibleLogicalRangeChange(onRange)

    return () => {
      ro.disconnect()
      chart.timeScale().unsubscribeVisibleTimeRangeChange(onRange)
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onRange)
      chart.remove()
      chartRef.current = null
      seriesRef.current = null
      volumeSeriesRef.current = null
    }
  }, [])

  // Swap the active series when the user toggles candle/line. Colors are set
  // separately (next effect) so toggling `showTrades` doesn't require
  // recreating the series and losing zoom/pan state.
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    if (seriesRef.current) {
      chart.removeSeries(seriesRef.current)
      seriesRef.current = null
      markersPluginRef.current = null
    }
    // New series → no tail history to diff against. The next setData effect
    // must do a full setData to seed the freshly-created series.
    lastWrittenTailRef.current = null
    if (chartType === 'candle') {
      seriesRef.current = chart.addSeries(CandlestickSeries, {
        priceLineVisible: false,
      })
    } else {
      seriesRef.current = chart.addSeries(AreaSeries, {
        lineWidth: 2,
        lineType: LineType.Curved,
        priceLineVisible: false,
      })
    }
    markersPluginRef.current = createSeriesMarkers(seriesRef.current, [])
  }, [chartType])

  // Falling-range red / rising-range green, matching PortfolioValueChart.
  const rangeIsUp = last - first >= 0

  // Color management. Two modes:
  //   showTrades ON  → monochrome ("traces don't compete with markers"):
  //     line   → flat gray fill+stroke
  //     candle → up=filled white, down=black-fill / white-outline
  //   showTrades OFF → trend-based:
  //     line   → green/red by whole-range delta
  //     candle → green/red per-bar
  useEffect(() => {
    const s = seriesRef.current
    if (!s) return
    if (chartType === 'candle') {
      const c = s as ISeriesApi<'Candlestick'>
      c.applyOptions({
        upColor: '#ffffff',
        downColor: '#000000',
        borderVisible: true,
        borderUpColor: '#ffffff',
        borderDownColor: '#ffffff',
        wickUpColor: '#ffffff',
        wickDownColor: '#ffffff',
      })
    } else {
      const a = s as ISeriesApi<'Area'>
      if (showTrades) {
        a.applyOptions({
          lineColor: NEUTRAL,
          topColor: 'rgba(161,161,170,0.25)',
          bottomColor: FILL_BOTTOM,
        })
      } else {
        a.applyOptions({
          lineColor: rangeIsUp ? UP : DOWN,
          topColor: rangeIsUp ? UP_FILL_TOP : DOWN_FILL_TOP,
          bottomColor: FILL_BOTTOM,
        })
      }
    }
  }, [chartType, showTrades, rangeIsUp])

  // lightweight-charts asserts strictly ascending unique timestamps. We can't
  // rely on input cleanliness — the bars cache historically has duplicate rows
  // (see 0002_deduplicate_equity_bars.sql), DST fall-back can collapse two ms
  // values onto the same chart-second, and async fetches can land out of order.
  // Dedupe by the *post-conversion* chart-time key (a Map, last-write-wins so
  // live-tick patches beat older cached versions of the same bucket), then
  // sort. Invalid bars are filtered first so a single NaN can't poison the run.
  const chartBars = useMemo(() => {
    const valid = bars.filter((b) =>
      typeof b.t === 'number' && isFinite(b.t) &&
      typeof b.o === 'number' && isFinite(b.o) &&
      typeof b.h === 'number' && isFinite(b.h) &&
      typeof b.l === 'number' && isFinite(b.l) &&
      typeof b.c === 'number' && isFinite(b.c),
    )
    if (valid.length === 0) return [] as Array<{ time: number; bar: BarPoint }>
    const byTime = new Map<number, BarPoint>()
    for (const b of valid) {
      byTime.set(toLocalChartTime(b.t) as unknown as number, b)
    }
    return Array.from(byTime.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([time, bar]) => ({ time, bar }))
  }, [bars])

  useEffect(() => {
    const s = seriesRef.current
    if (!s) return
    if (chartBars.length === 0) {
      lastWrittenTailRef.current = null
      paintedTimeRef.current = 0
      // Clear stale candles immediately on symbol change instead of leaving the
      // previous symbol's data on screen until new bars arrive.
      ;(s as ISeriesApi<'Candlestick' | 'Area' | 'Line'>).setData([])
      return
    }

    const newLast = chartBars[chartBars.length - 1]
    const prev = lastWrittenTailRef.current

    // Decide if this update is a tail mutation or a single-bar append — both
    // map to `series.update()`, which leaves the rest of the series (and the
    // user's pan/zoom/hover) untouched. Anything else (initial seed, prepended
    // older bars, sort/dedupe reshuffle, chartType swap) needs a full setData.
    let incremental = false
    if (prev != null) {
      if (
        chartBars.length === prev.length &&
        newLast.time === prev.lastTime &&
        newLast.bar !== prev.lastBar
      ) {
        // Trailing bar mutated in place (same bucket, new OHLC).
        incremental = true
      } else if (
        chartBars.length === prev.length + 1 &&
        chartBars[chartBars.length - 2].time === prev.lastTime
      ) {
        // Exactly one new bar appended; previous tail unchanged.
        incremental = true
      }
    }

    if (incremental) {
      if (chartType === 'candle') {
        ;(s as ISeriesApi<'Candlestick'>).update({
          time: newLast.time as unknown as UTCTimestamp,
          open: newLast.bar.o,
          high: newLast.bar.h,
          low: newLast.bar.l,
          close: newLast.bar.c,
        })
      } else {
        ;(s as ISeriesApi<'Area'>).update({
          time: newLast.time as unknown as UTCTimestamp,
          value: newLast.bar.c,
        })
      }
    } else {
      if (chartType === 'candle') {
        ;(s as ISeriesApi<'Candlestick'>).setData(
          chartBars.map<CandlestickData>(({ time, bar }) => ({
            time: time as unknown as UTCTimestamp,
            open: bar.o,
            high: bar.h,
            low: bar.l,
            close: bar.c,
          })),
        )
      } else {
        ;(s as ISeriesApi<'Area'>).setData(
          chartBars.map<AreaData>(({ time, bar }) => ({
            time: time as unknown as UTCTimestamp,
            value: bar.c,
          })),
        )
      }
      // Skip fitContent when we just prepended older bars during a pan —
      // re-fitting would zoom the chart back out and undo the user's gesture.
      if (skipFitRef.current) {
        skipFitRef.current = false
      } else {
        const ts = chartRef.current?.timeScale()
        if (ts) {
          const lastIdx = chartBars.length - 1
          if (!hasPaddedRef.current && lastIdx > 0) {
            hasPaddedRef.current = true
            // First load: horizontally center the latest candle — show the
            // loaded bars in the left half and equal empty space in the right
            // half so the most recent bar sits at the visible window's midpoint.
            ts.setVisibleLogicalRange({ from: 0, to: 2 * lastIdx })
          } else {
            ts.fitContent()
          }
        }
      }
      // Clear the hovered crosshair on full redraws — coordinates may no
      // longer map to the same bar. Incremental updates preserve hover.
      setHovered(null)
    }

    lastWrittenTailRef.current = {
      length: chartBars.length,
      lastTime: newLast.time,
      lastBar: newLast.bar,
    }
    // Plain assignment (not max): a symbol/interval reseed legitimately moves
    // the tail backwards, and direct tick paints must not stay blocked.
    paintedTimeRef.current = newLast.bar.t
    setOverlayTick((t) => t + 1)
  }, [chartBars, chartType])

  // Volume overlay. Driven by the `volume` entry in the indicator config
  // (toggle + colors live in the indicator menu, persisted like every other
  // indicator). Unlike the engine-computed lines/levels, this is rendered here
  // straight off each bar's `v` — colored by candle direction at 50% alpha so
  // the bars read as a backdrop, not competing with the price series. Cleared
  // when the toggle is off. Re-runs on `chartBars` (covers the 1s live-tick
  // flush; the direct per-tick paint intentionally skips volume since we have
  // no per-tick volume to aggregate).
  const volumeIndicator = useMemo(
    () =>
      indicatorConfig.indicators.find((i) => i.type === 'volume') as
        | UserVolumeIndicator
        | undefined,
    [indicatorConfig],
  )
  const volumeEnabled = !!volumeIndicator?.enabled
  const volUpColor = hexToRgba(volumeIndicator?.upColor ?? '#10b981', 0.5)
  const volDownColor = hexToRgba(volumeIndicator?.downColor ?? '#ef4444', 0.5)
  useEffect(() => {
    const vs = volumeSeriesRef.current
    if (!vs) return
    if (!volumeEnabled || chartBars.length === 0) {
      vs.setData([])
      return
    }
    vs.setData(
      chartBars.map<HistogramData>(({ time, bar }) => ({
        time: time as unknown as UTCTimestamp,
        value: bar.v,
        color: bar.c >= bar.o ? volUpColor : volDownColor,
      })),
    )
  }, [chartBars, volumeEnabled, volUpColor, volDownColor])

  // Per-fill markers: every opening-side fill (BUY for long cycles, SELL for
  // short cycles) draws an arrow in the cycle's bias direction; every
  // closing-side fill draws a circle dot. Color encodes outcome (green win /
  // red loss / gray still-open) so a glance tells you whether the position
  // worked. Cleared when `showTrades` is off. Re-applied on chartType swap
  // because that recreates the series.
  useEffect(() => {
    const plugin = markersPluginRef.current
    if (!plugin) return
    if (!showTrades) {
      plugin.setMarkers([])
      return
    }
    const markers: SeriesMarker<UTCTimestamp>[] = []
    for (const cycle of cycles) {
      const bullish = cycle.bias === 'bullish'
      for (const p of cycle.points) {
        if (p.tx.side === cycle.openSide) {
          markers.push({
            time: p.time,
            position: bullish ? 'belowBar' : 'aboveBar',
            shape: bullish ? 'arrowUp' : 'arrowDown',
            color: cycle.color,
          })
        } else {
          markers.push({
            time: p.time,
            position: 'inBar',
            shape: 'circle',
            color: cycle.color,
          })
        }
      }
    }
    // Markers must be sorted strictly ascending by time.
    markers.sort((a, b) => (a.time as number) - (b.time as number))
    plugin.setMarkers(markers)
  }, [cycles, chartType, showTrades])

  // TradingView-style "change interval" modal. Typing any digit while the
  // chart has focus opens a small overlay showing what's been typed; Enter
  // commits, Esc closes, Backspace edits. Listener runs in the capture phase
  // so the global search-bar handler never sees these keys.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // In grid mode several charts listen on document — only the active cell
      // (or the maximized one, which owns the screen) reacts, so a keystroke
      // changes the timeframe/ticker of the clicked-active chart and no other.
      if (gridCell && !active && !maximized) return
      const target = e.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target.isContentEditable
        ) {
          return
        }
      }
      // Option+R (Alt+R) resets the chart's pan/zoom — fit all loaded bars
      // and re-enable price-scale auto-fit. `e.code` is checked because Mac's
      // Option modifier remaps `e.key` to a special character (`®`).
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyR') {
        e.preventDefault()
        e.stopPropagation()
        const chart = chartRef.current
        if (chart) {
          chart.timeScale().fitContent()
          chart.priceScale('right').applyOptions({ autoScale: true })
        }
        return
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const k = e.key
      if (intervalInput !== null) {
        if (k === 'Enter') {
          e.preventDefault()
          e.stopPropagation()
          const parsed = parseIntervalInput(intervalInput)
          if (parsed) commitInterval(parsed)
          setIntervalInput(null)
          return
        }
        if (k === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          setIntervalInput(null)
          return
        }
        if (k === 'Backspace') {
          e.preventDefault()
          e.stopPropagation()
          setIntervalInput((prev) => {
            if (prev == null || prev.length <= 1) return null
            return prev.slice(0, -1)
          })
          return
        }
        if (/^[0-9]$/.test(k) || /^[mhd]$/i.test(k)) {
          e.preventDefault()
          e.stopPropagation()
          setIntervalInput((prev) => (prev ?? '') + k.toLowerCase())
        }
        return
      }
      // Modal closed — only digits open the interval modal (TradingView behavior).
      if (/^[0-9]$/.test(k)) {
        e.preventDefault()
        e.stopPropagation()
        setIntervalInput(k)
        return
      }
      // Letters open the in-pane ticker search on the active grid cell. Mirrors
      // the interval modal: seed with the typed letter and swallow the key so
      // the header search never sees it.
      if (onSymbolChange && active && symbolSearch === null && /^[a-zA-Z]$/.test(k)) {
        e.preventDefault()
        e.stopPropagation()
        setSymbolSearch(k.toUpperCase())
        return
      }
      // Esc exits maximize when no modal is open.
      if (k === 'Escape' && maximized) {
        e.preventDefault()
        e.stopPropagation()
        setMaximized(false)
      }
    }
    document.addEventListener('keydown', onKey, { capture: true })
    return () => {
      document.removeEventListener('keydown', onKey, { capture: true })
    }
  }, [intervalInput, maximized, active, onSymbolChange, symbolSearch])

  useEffect(() => {
    if (!maximized) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [maximized])

  // Measurement tool. Shift+click anchors the start (x, y, price). While
  // active and not yet locked, the end follows the cursor — the chip shows
  // live % vs the start. A plain click locks the end at its current
  // position; Esc clears. Another shift+click resets to a new start.
  const [measure, setMeasure] = useState<{
    startX: number
    startY: number
    startPrice: number
    endX: number
    endY: number
    endPrice: number
    locked: boolean
  } | null>(null)

  function pointToPrice(e: React.PointerEvent<HTMLDivElement>) {
    const series = seriesRef.current
    if (!series) return null
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const price = series.coordinateToPrice(y)
    if (price == null) return null
    return { x, y, price: price as number }
  }

  function onMeasurePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.shiftKey) {
      const p = pointToPrice(e)
      if (!p) return
      e.preventDefault()
      setMeasure({
        startX: p.x,
        startY: p.y,
        startPrice: p.price,
        endX: p.x,
        endY: p.y,
        endPrice: p.price,
        locked: false,
      })
      return
    }
    if (!measure) return
    // Plain click on a locked measurement clears it. Plain click on an
    // in-progress (unlocked) measurement locks the end at the cursor.
    if (measure.locked) {
      e.preventDefault()
      setMeasure(null)
      return
    }
    const p = pointToPrice(e)
    if (!p) return
    e.preventDefault()
    setMeasure({ ...measure, endX: p.x, endY: p.y, endPrice: p.price, locked: true })
  }

  function onMeasurePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!measure || measure.locked) return
    const p = pointToPrice(e)
    if (!p) return
    setMeasure({ ...measure, endX: p.x, endY: p.y, endPrice: p.price })
  }

  // Esc clears measurement. Registered alongside the interval-modal handler,
  // but in the bubble phase so the modal-close logic gets first crack.
  useEffect(() => {
    if (!measure) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && intervalInput === null) {
        setMeasure(null)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [measure, intervalInput])

  const hasData = bars.length > 0
  const errCode = isError && error instanceof ApiError ? error.code : undefined
  const isUnconfigured =
    errCode === 'platform_not_attached' || errCode === 'reauth_required'

  // When the crosshair lands near a fill, surface it in the floating tooltip.
  // Skipped entirely when trades are hidden — no markers means no fills to
  // surface. Tolerance scales with interval so 1m windows don't merge
  // unrelated fills while 1d windows still let you hover-match a whole-day bar.
  const hoveredFills = useMemo(() => {
    if (!showTrades || hovered == null) return []
    const tolMs = FILL_TOLERANCE_MS[interval]
    const target = hovered.tMs
    const matches: Transaction[] = []
    for (const cycle of cycles) {
      for (const p of cycle.points) {
        const t = fromLocalChartTime(p.time as unknown as number)
        if (Math.abs(t - target) <= tolMs) matches.push(p.tx)
      }
    }
    return matches
  }, [hovered, cycles, interval, showTrades])
  // HTML overlay positions for each cycle's lifetime band + (closed only)
  // P/L chip. Recomputed whenever `overlayTick` bumps (resize, pan/zoom,
  // chartType swap, data refresh) so they track the chart's pixel mapping.
  // Computing here in render — instead of an effect that pokes the DOM —
  // keeps positions in lockstep with React's commit phase.
  interface BandOverlay {
    key: string
    left: number
    width: number
    color: string
  }
  interface ChipOverlay {
    key: string
    left: number
    top: number
    text: string
    color: string
  }
  const overlays = useMemo<{ bands: BandOverlay[]; chips: ChipOverlay[] }>(() => {
    // overlayTick is intentionally read so the memo re-runs on chart events.
    void overlayTick
    const chart = chartRef.current
    const s = seriesRef.current
    if (!chart || !s) return { bands: [], chips: [] }
    const ts = chart.timeScale()
    const bands: BandOverlay[] = []
    const chips: ChipOverlay[] = []
    const lastBarTime = lastBarT != null ? toLocalChartTime(lastBarT) : null
    for (const cycle of cycles) {
      if (cycle.points.length === 0) continue
      // Open cycles extend the band to the right edge of the data — the
      // position is still live, so "end" hasn't happened yet.
      const startT = cycle.points[0].time
      const endT =
        cycle.isOpen && lastBarTime != null
          ? lastBarTime
          : cycle.points[cycle.points.length - 1].time
      const x1 = ts.timeToCoordinate(startT)
      const x2 = ts.timeToCoordinate(endT)
      if (x1 == null || x2 == null) continue
      const left = Math.min(x1, x2)
      const width = Math.max(1, Math.abs(x2 - x1))
      bands.push({ key: `band-${cycle.key}`, left, width, color: cycle.color })

      if (!cycle.isOpen) {
        const lastP = cycle.points[cycle.points.length - 1]
        const cx = ts.timeToCoordinate(lastP.time)
        const cy = s.priceToCoordinate(lastP.value)
        if (cx == null || cy == null) continue
        const sign = cycle.realizedPL >= 0 ? '+' : '−'
        const absPL = Math.abs(cycle.realizedPL)
        const pctText =
          cycle.costBasis > 0
            ? ` (${sign}${((absPL / cycle.costBasis) * 100).toFixed(1)}%)`
            : ''
        chips.push({
          key: `chip-${cycle.key}`,
          left: cx,
          top: cy,
          text: `${sign}${fmtPrice(absPL)}${pctText}`,
          color: cycle.color,
        })
      }
    }
    return { bands, chips }
  }, [cycles, overlayTick, lastBarT])
  // Indicator labels at the right edge of each active line / price level.
  interface IndicatorLabelOverlay {
    key: string
    left: number
    top: number
    text: string
    color: string
  }
  const indicatorLabels = useMemo<IndicatorLabelOverlay[]>(() => {
    void overlayTick
    const chart = chartRef.current
    const s = seriesRef.current
    if (!chart || !s) return [] as IndicatorLabelOverlay[]
    const ts = chart.timeScale()
    const labels: IndicatorLabelOverlay[] = []
    const visibleRange = ts.getVisibleRange()
    const visibleFrom =
      visibleRange != null ? Number(visibleRange.from) : null
    const visibleTo =
      visibleRange != null ? Number(visibleRange.to) : null

    for (const line of indicatorOutput.lines) {
      const lastPoint = line.points[line.points.length - 1]
      if (!lastPoint || lastPoint.value == null) continue
      const chartTime = toLocalChartTime(lastPoint.t)
      const x = ts.timeToCoordinate(chartTime)
      const y = s.priceToCoordinate(lastPoint.value)
      if (x == null || y == null) continue
      labels.push({
        key: `ind-label-${line.id}`,
        left: x,
        top: y,
        text: line.title,
        color: line.color,
      })
    }
    for (const level of indicatorOutput.priceLevels) {
      let anchor: { chartTime: UTCTimestamp; value: number } | null = null
      for (const segment of level.segments) {
        if (!Number.isFinite(segment.fromT) || !Number.isFinite(segment.toT)) {
          continue
        }
        const fromChartTime = Number(toLocalChartTime(segment.fromT))
        const toChartTime = Number(toLocalChartTime(segment.toT))
        if (!Number.isFinite(fromChartTime) || !Number.isFinite(toChartTime)) {
          continue
        }
        if (visibleFrom != null && visibleTo != null) {
          if (toChartTime < visibleFrom || fromChartTime > visibleTo) continue
          const chartTime = Math.min(toChartTime, visibleTo)
          if (anchor == null || chartTime > Number(anchor.chartTime)) {
            anchor = {
              chartTime: chartTime as UTCTimestamp,
              value: segment.value,
            }
          }
          continue
        }
        if (anchor == null || toChartTime > Number(anchor.chartTime)) {
          anchor = {
            chartTime: toChartTime as UTCTimestamp,
            value: segment.value,
          }
        }
      }
      if (!anchor) continue
      const x = ts.timeToCoordinate(anchor.chartTime)
      const y = s.priceToCoordinate(anchor.value)
      if (x == null || y == null) continue
      labels.push({
        key: `ind-label-${level.id}`,
        left: x,
        top: y,
        text: level.title,
        color: level.color,
      })
    }
    return labels
  }, [indicatorOutput.lines, indicatorOutput.priceLevels, overlayTick])


  // Extended-hours session bands: faint shaded backgrounds for pre-market
  // (04:00–09:30 ET) and post-market (16:00–20:00 ET). Skipped on 1d.
  //
  // Anchor to the true session-boundary TIMES, not to bars. The previous
  // approach classified each bar by its NY hour and shaded the run of bars,
  // which breaks on intervals whose buckets straddle a boundary: a 1h bar is
  // bucketed to the UTC hour, so the 09:00–10:00 candle spans both pre-market
  // (09:00–09:30) and the regular open (09:30–10:00). Classifying that whole
  // candle as "pre" overshot the shading 30 min into the regular session.
  // Mapping the boundary itself (09:30) and interpolating WITHIN the
  // straddling candle is interval-agnostic and DST-safe.
  interface SessionBandOverlay {
    key: string
    left: number
    width: number
    kind: 'pre' | 'post'
  }
  interface SessionWindow {
    startMs: number
    endMs: number
    kind: 'pre' | 'post'
  }
  // Step 1: enumerate the extended-hours windows (real epoch ms) covering the
  // loaded bars — one pre-market [04:00,09:30) and one post-market
  // [16:00,20:00) per distinct NY trading date present in the data. O(days).
  const sessionWindows = useMemo<SessionWindow[]>(() => {
    if (interval === '1d' || bars.length === 0) return []
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
    const nyParts = (ms: number) => {
      const p = Object.fromEntries(fmt.formatToParts(ms).map((x) => [x.type, x.value]))
      let h = Number(p.hour)
      if (h === 24) h = 0 // Intl midnight quirk
      return { y: Number(p.year), mo: Number(p.month), d: Number(p.day), h, mi: Number(p.minute) }
    }
    // NY wall-clock → UTC epoch ms, DST-safe (guess then correct by the actual
    // NY offset at that instant — mirrors the server's nyToUtcMs).
    const nyWallToUtc = (y: number, mo: number, d: number, h: number, mi: number) => {
      const guess = Date.UTC(y, mo - 1, d, h, mi)
      const p = nyParts(guess)
      const asUtc = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi)
      return guess - (asUtc - guess)
    }
    const dates = new Map<string, { y: number; mo: number; d: number }>()
    for (const b of bars) {
      const p = nyParts(b.t)
      const key = `${p.y}-${p.mo}-${p.d}`
      if (!dates.has(key)) dates.set(key, { y: p.y, mo: p.mo, d: p.d })
    }
    const wins: SessionWindow[] = []
    for (const { y, mo, d } of dates.values()) {
      wins.push({ kind: 'pre', startMs: nyWallToUtc(y, mo, d, 4, 0), endMs: nyWallToUtc(y, mo, d, 9, 30) })
      wins.push({ kind: 'post', startMs: nyWallToUtc(y, mo, d, 16, 0), endMs: nyWallToUtc(y, mo, d, 20, 0) })
    }
    return wins
  }, [bars, interval])

  // Step 2: map each window's boundary times to pixel coords on chart events.
  // timeToCoordinate only resolves exact bar times, so for a boundary that
  // falls inside (or between) bars we locate the bar whose [t, t+step) range
  // contains it and interpolate its position across that candle's pixel width.
  const sessionBands = useMemo<SessionBandOverlay[]>(() => {
    void overlayTick
    if (sessionWindows.length === 0) return []
    const chart = chartRef.current
    if (!chart) return []
    const ts = chart.timeScale()
    const visible = ts.getVisibleRange()
    const visFrom = visible != null ? Number(visible.from) : null
    const visTo = visible != null ? Number(visible.to) : null
    const step = INTERVAL_MS[interval] ?? 60_000
    // Bar pixel width from the first two loaded bars (uniform index spacing).
    let barW = 0
    if (bars.length >= 2) {
      const a = ts.timeToCoordinate(toLocalChartTime(bars[0].t))
      const b = ts.timeToCoordinate(toLocalChartTime(bars[1].t))
      if (a != null && b != null) barW = Math.abs(b - a)
    }
    const half = barW / 2
    // Pixel x for an arbitrary boundary time. Clamps past either data edge;
    // otherwise finds the bar containing the boundary and places it the
    // fractional way across that candle (so 09:30 lands at the centre of a
    // straddling 1h candle, the left edge of an aligned 15m candle, etc.).
    const xAtTime = (targetMs: number): number | null => {
      const first = bars[0]
      const last = bars[bars.length - 1]
      if (targetMs <= first.t) {
        const x = ts.timeToCoordinate(toLocalChartTime(first.t))
        return x == null ? null : x - half
      }
      if (targetMs >= last.t + step) {
        const x = ts.timeToCoordinate(toLocalChartTime(last.t))
        return x == null ? null : x + half
      }
      let lo = 0
      let hi = bars.length - 1
      let i = 0
      while (lo <= hi) {
        const mid = (lo + hi) >> 1
        if (bars[mid].t <= targetMs) {
          i = mid
          lo = mid + 1
        } else {
          hi = mid - 1
        }
      }
      const xi = ts.timeToCoordinate(toLocalChartTime(bars[i].t))
      if (xi == null) return null
      const frac = Math.min(1, Math.max(0, (targetMs - bars[i].t) / step))
      return xi - half + frac * barW
    }
    const bands: SessionBandOverlay[] = []
    for (const w of sessionWindows) {
      // Skip windows entirely outside the visible range.
      if (visFrom != null && visTo != null) {
        const s = Number(toLocalChartTime(w.startMs))
        const e = Number(toLocalChartTime(w.endMs))
        if (e < visFrom || s > visTo) continue
      }
      const xl = xAtTime(w.startMs)
      const xr = xAtTime(w.endMs)
      if (xl == null || xr == null) continue
      const left = Math.min(xl, xr)
      const width = Math.abs(xr - xl)
      if (width < 1) continue
      bands.push({ key: `sess-${w.kind}-${w.startMs}`, left, width, kind: w.kind })
    }
    return bands
  }, [sessionWindows, bars, interval, overlayTick])
  return (
    <>
    <div
      className={
        maximized
          ? 'fixed inset-0 z-50 bg-zinc-950 p-4 flex flex-col'
          : gridCell
            ? 'flex flex-col min-h-0 min-w-0 flex-1 px-3 pt-2 pb-2'
            : fill
              ? 'flex flex-col min-h-0 flex-1 lg:flex-none px-4 pt-4 pb-3 border-b border-zinc-800'
              : 'px-4 py-4 border-b border-zinc-800'
      }
    >
      <div
        className={`flex flex-row items-start justify-between gap-3 mb-3 ${
          panelOpen && !maximized ? 'max-lg:hidden' : ''
        }`}
      >
        <ChartHeader
          symbol={symbol}
          hasData={hasData}
          isLoading={isLoading}
          first={first}
          last={last}
          hovered={hovered}
        />
      <div className="flex flex-row gap-1 shrink-0 items-start flex-wrap">
        <div className="flex flex-col gap-1 shrink-0">
          <div className="flex gap-1 items-center flex-wrap justify-end">
            <button
              type="button"
              onClick={() => setShowTrades((v) => !v)}
              aria-pressed={showTrades}
              title={showTrades ? 'Hide trades' : 'Show trades'}
              className={`px-2 py-1 text-[11px] rounded font-mono border transition-colors ${
                showTrades
                  ? 'bg-sky-500/15 text-sky-300 border-sky-500/30'
                  : 'text-zinc-400 hover:text-zinc-100 border-transparent'
              }`}
            >
              Trades
            </button>
            <button
              type="button"
              onClick={() =>
                setChartType((t) => (t === 'candle' ? 'line' : 'candle'))
              }
              title={
                chartType === 'candle'
                  ? 'Switch to line chart'
                  : 'Switch to candle chart'
              }
              aria-label={
                chartType === 'candle'
                  ? 'Switch to line chart'
                  : 'Switch to candle chart'
              }
              className="px-1.5 py-1 rounded border border-transparent text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
            >
              {chartType === 'candle' ? (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <line x1="4" y1="2" x2="4" y2="12" stroke="currentColor" strokeWidth="1" />
                  <rect x="2.5" y="4" width="3" height="5" fill="currentColor" />
                  <line x1="10" y1="3" x2="10" y2="11" stroke="currentColor" strokeWidth="1" />
                  <rect x="8.5" y="5" width="3" height="4" fill="currentColor" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <polyline
                    points="1,10 5,6 9,8 13,3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
            {!popout && (
              <button
                type="button"
                onClick={openPopout}
                disabled={!symbol}
                title="Detach into a new window"
                aria-label="Detach chart into a new window"
                className="px-1.5 py-1 rounded border border-transparent text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path
                    d="M6 2.5H2.5v9h9V8"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.5 2.5h3v3M11.5 2.5 6.75 7.25"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={() => setMaximized((v) => !v)}
              aria-pressed={maximized}
              title={maximized ? 'Exit fullscreen' : 'Maximize chart'}
              aria-label={maximized ? 'Exit fullscreen' : 'Maximize chart'}
              className="px-1.5 py-1 rounded border border-transparent text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
            >
              {maximized ? (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path
                    d="M5.5 1.5v3h-3M8.5 1.5v3h3M5.5 12.5v-3h-3M8.5 12.5v-3h3"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path
                    d="M1.5 5V1.5H5M9 1.5h3.5V5M12.5 9v3.5H9M5 12.5H1.5V9"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              )}
            </button>
            {/* Refresh / clear-cache button hidden for now */}
            {false && (
              <button
                type="button"
                onClick={handleClearChartCache}
                disabled={clearingBars || !symbol}
                title={`Clear cached ${interval} bars for ${symbol || 'this symbol'} and refetch`}
                aria-label="Clear chart cache and refetch"
                className="px-1.5 py-1 rounded border border-transparent text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                  className={clearingBars ? 'animate-spin' : undefined}
                >
                  <path
                    d="M2 7a5 5 0 0 1 8.5-3.5L12 5M12 2v3H9M12 7a5 5 0 0 1-8.5 3.5L2 9M2 12V9h3"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={() => setIndicatorModalOpen(true)}
              title="Chart indicators"
              className="px-2 py-1 text-[11px] rounded font-mono text-zinc-400 hover:text-zinc-100 transition-colors flex items-center gap-1"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <polyline points="3,9 9,5 14,9 21,3" stroke="currentColor" strokeWidth="1.75" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="3" y="16" width="3.6" height="5" rx="0.8" fill="currentColor"/>
                <rect x="9.2" y="13" width="3.6" height="8" rx="0.8" fill="currentColor"/>
                <rect x="15.4" y="10" width="3.6" height="11" rx="0.8" fill="currentColor"/>
              </svg>
            </button>
            <div ref={intervalMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setIntervalMenuOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={intervalMenuOpen}
                title="Change interval"
                className="px-2 py-1 text-[11px] rounded font-mono border bg-sky-500/15 text-sky-300 border-sky-500/30 hover:bg-sky-500/25 transition-colors flex items-center gap-1"
              >
                {interval}
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M3 5 L6 8 L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {intervalMenuOpen && (
                <div
                  role="listbox"
                  className="absolute right-0 top-full mt-1 z-30 min-w-[72px] bg-zinc-900 border border-zinc-700 rounded shadow-xl py-1"
                >
                  {INTERVALS.map((iv) => (
                    <button
                      key={iv}
                      type="button"
                      role="option"
                      aria-selected={iv === interval}
                      onClick={() => {
                        commitInterval(iv)
                        setIntervalMenuOpen(false)
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs font-mono transition-colors ${
                        iv === interval
                          ? 'bg-sky-500/15 text-sky-300'
                          : 'text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100'
                      }`}
                    >
                      {iv}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      <div
        className={
          maximized || gridCell
            ? 'relative flex-1 min-h-0 overflow-hidden'
            : fill
              ? 'relative overflow-hidden flex-1 min-h-0 lg:flex-none lg:h-[calc(100dvh-16rem)]'
              : 'relative overflow-hidden h-[calc(100dvh-12rem)] sm:h-[400px] lg:h-[calc(100dvh-16rem)]'
        }
        onContextMenu={(e) => e.preventDefault()}
        onPointerDown={onMeasurePointerDown}
        onPointerMove={onMeasurePointerMove}
      >
        <div ref={containerRef} className="absolute inset-0" />
        {panelOpen && !maximized && (
          <ChartPriceOverlay
            symbol={symbol}
            hasData={hasData}
            last={last}
            className="lg:hidden"
          />
        )}
        <IndicatorRenderer
          chart={chartRef.current}
          seriesRef={seriesRef.current}
          output={indicatorOutput}
          overlayTick={overlayTick}
        />
        <ChartRulesDashboard symbol={symbol} />
        {/* Extended-hours session shading: pre-market (04:00–09:30 ET) and
            post-market (16:00–20:00 ET). Sits below all interactive overlays. */}
        {sessionBands.map((b) => (
          <div
            key={b.key}
            aria-hidden
            className="absolute top-0 bottom-0 pointer-events-none z-[2]"
            style={{
              left: b.left,
              width: b.width,
              background:
                b.kind === 'pre'
                  ? 'rgba(59,130,246,0.05)'
                  : 'rgba(168,85,247,0.05)',
            }}
          />
        ))}
        {/* Lifetime bands: one faint tinted band per cycle spanning first→last
            fill (or first→right-edge for still-open cycles). Sits under the
            crosshair tooltip and chip overlays. */}
        {showTrades && overlays.bands.map((b) => (
          <div
            key={b.key}
            aria-hidden
            className="absolute top-0 bottom-0 pointer-events-none z-10"
            style={{
              left: b.left,
              width: b.width,
              background: hexToRgba(b.color, 0.08),
              borderLeft: `1px dashed ${hexToRgba(b.color, 0.5)}`,
              borderRight: `1px dashed ${hexToRgba(b.color, 0.5)}`,
            }}
          />
        ))}
        {/* P/L chip on the final close fill of each closed cycle. Anchored to
            the close marker's (x, y) so it tracks pan/zoom. `z-20` sits above
            lightweight-charts' canvas (which establishes its own stacking
            context) so candle bodies don't paint over the chip. */}
        {showTrades && overlays.chips.map((c) => (
          <div
            key={c.key}
            aria-hidden
            className="absolute pointer-events-none z-20 font-mono text-[10px] px-1.5 py-0.5 rounded border whitespace-nowrap shadow-lg"
            style={{
              left: c.left,
              top: c.top,
              transform: 'translate(10px, -50%)',
              color: c.color,
              background: '#09090b',
              borderColor: hexToRgba(c.color, 0.55),
            }}
          >
            {c.text}
          </div>
        ))}
        {/* Indicator labels at the right edge of each active line / level. */}
        {indicatorLabels.map((l) => (
          <div
            key={l.key}
            aria-hidden
            className="absolute pointer-events-none z-15 font-mono text-[11px] whitespace-nowrap"
            style={{
              left: l.left + INDICATOR_LABEL_X_OFFSET,
              top: l.top,
              transform: 'translate(0, -50%)',
              color: l.color,
            }}
          >
            {l.text}
          </div>
        ))}
        {measure && (() => {
          const delta = measure.endPrice - measure.startPrice
          const pct = measure.startPrice !== 0 ? (delta / measure.startPrice) * 100 : 0
          const up = delta >= 0
          const color = up ? UP : DOWN
          const left = Math.min(measure.startX, measure.endX)
          const top = Math.min(measure.startY, measure.endY)
          const width = Math.max(1, Math.abs(measure.endX - measure.startX))
          const height = Math.max(1, Math.abs(measure.endY - measure.startY))
          const sign = up ? '+' : ''
          return (
            <>
              <div
                aria-hidden
                className="absolute pointer-events-none z-30"
                style={{
                  left,
                  top,
                  width,
                  height,
                  background: hexToRgba(color, 0.1),
                  border: `1px dashed ${hexToRgba(color, 0.6)}`,
                }}
              />
              {/* Start marker — small dot at the anchor so the user can see
                  where they clicked even after the cursor moves. */}
              <div
                aria-hidden
                className="absolute pointer-events-none z-30 rounded-full"
                style={{
                  left: measure.startX - 4,
                  top: measure.startY - 4,
                  width: 8,
                  height: 8,
                  background: color,
                  boxShadow: '0 0 0 2px #09090b',
                }}
              />
              <div
                aria-hidden
                className="absolute pointer-events-none z-30 font-mono text-[11px] px-2 py-1 rounded border shadow-lg whitespace-nowrap leading-tight"
                style={{
                  left: measure.endX,
                  top: measure.endY,
                  transform: `translate(${measure.endX > measure.startX ? '10px' : 'calc(-100% - 10px)'}, ${measure.endY > measure.startY ? '10px' : 'calc(-100% - 10px)'})`,
                  color,
                  background: '#09090b',
                  borderColor: hexToRgba(color, 0.55),
                }}
              >
                <div>
                  {sign}{pct.toFixed(2)}%
                </div>
                <div className="text-zinc-400 text-[10px]">
                  {sign}{fmtPrice(delta)} · {fmtPrice(measure.startPrice)} → {fmtPrice(measure.endPrice)}
                </div>
              </div>
            </>
          )
        })()}
        {hoveredFills.length > 0 && cursorXY && (
          <FillTooltip fills={hoveredFills} cursor={cursorXY} />
        )}
        {isLoading && !hasData && (
          <div className="absolute inset-0 animate-pulse bg-zinc-900/30" />
        )}
        {!isLoading && !hasData && !isError && (
          <div className="absolute inset-0 grid place-items-center text-xs text-zinc-500">
            No price data for {symbol} in this range.
          </div>
        )}
        {isError && (
          <div className="absolute inset-0 grid place-items-center text-center text-xs text-zinc-500 px-6">
            {isUnconfigured ? (
              <span>Connect a Schwab account to view price history.</span>
            ) : (
              <span>
                Couldn’t load chart.{' '}
                <button
                  type="button"
                  className="text-emerald-400 hover:text-emerald-300 underline"
                  onClick={() => refetch()}
                >
                  Retry
                </button>
              </span>
            )}
          </div>
        )}
        {intervalInput !== null && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center"
            onClick={() => setIntervalInput(null)}
          >
            <div
              className="bg-zinc-900/95 backdrop-blur border border-zinc-700 rounded-lg shadow-2xl px-6 py-4 min-w-[220px]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">
                Change interval
              </div>
              <div className="font-mono text-2xl text-zinc-100 leading-tight min-h-[1.6rem]">
                {intervalInput}
                <span className="inline-block w-[2px] h-5 align-middle bg-zinc-400 ml-0.5 animate-pulse" />
              </div>
              {(() => {
                const parsed = parseIntervalInput(intervalInput)
                if (parsed) {
                  return (
                    <div className="text-[11px] text-emerald-400 mt-1 font-mono">
                      → {parsed}
                    </div>
                  )
                }
                if (intervalInput.length > 0) {
                  return (
                    <div className="text-[11px] text-amber-400 mt-1">
                      Available: 1m, 5m, 15m, 1h, 1d
                    </div>
                  )
                }
                return null
              })()}
              <div className="text-[11px] text-zinc-500 mt-2">
                <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-zinc-300 font-mono text-[10px]">
                  Enter
                </kbd>{' '}
                apply ·{' '}
                <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-zinc-300 font-mono text-[10px]">
                  Esc
                </kbd>{' '}
                cancel
              </div>
            </div>
          </div>
        )}
        {symbolSearch !== null && onSymbolChange && (
          <div
            className="absolute inset-0 z-20 flex items-start justify-center pt-10"
            onClick={() => setSymbolSearch(null)}
          >
            <div
              className="bg-zinc-900/95 backdrop-blur border border-zinc-700 rounded-lg shadow-2xl p-3 w-[260px] max-w-[88%]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">
                Change symbol
              </div>
              <SearchBar
                key={symbolSearch}
                captureTyping={false}
                autoFocus
                seedQuery={symbolSearch}
                onClose={() => setSymbolSearch(null)}
                onSelect={(s) => {
                  onSymbolChange(s)
                  setSymbolSearch(null)
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
    <IndicatorSettingsModal
      isOpen={indicatorModalOpen}
      onClose={() => setIndicatorModalOpen(false)}
      interval={interval}
      currentConfig={indicatorConfig}
      onSave={saveIndicatorConfig}
    />
    </>
  )
}

// Floating tooltip pinned near the crosshair, listing the fills that landed
// on the hovered bar. Flips to the left/below side of the cursor when it would
// otherwise overflow the chart container.
function FillTooltip({ fills, cursor }: { fills: Transaction[]; cursor: CursorXY }) {
  const OFFSET = 12
  // Width is fluid; height is 320px on mobile and 460px on >=sm. Thresholds are
  // conservative so the tooltip flips before it would clip on either size.
  const flipX = cursor.x > 320
  const flipY = cursor.y > 220
  const style: React.CSSProperties = {
    left: flipX ? undefined : cursor.x + OFFSET,
    right: flipX ? 'calc(100% - ' + (cursor.x - OFFSET) + 'px)' : undefined,
    top: flipY ? undefined : cursor.y + OFFSET,
    bottom: flipY ? 'calc(100% - ' + (cursor.y - OFFSET) + 'px)' : undefined,
  }
  return (
    <div
      className="absolute z-10 pointer-events-none bg-zinc-900/95 backdrop-blur border border-zinc-700 rounded shadow-lg px-2.5 py-1.5 font-mono text-[11px] min-w-[140px]"
      style={style}
    >
      {fills.slice(0, 6).map((tx) => {
        const isOpt =
          tx.instrumentType === 'OPTION' ||
          (tx.symbol ? isOptionSymbol(tx.symbol) : false)
        const contract = isOpt && tx.symbol ? fmtOsi(tx.symbol) : tx.symbol
        return (
          <div
            key={tx.id}
            className={tx.side === 'BUY' ? 'text-emerald-400' : 'text-red-400'}
          >
            {tx.side} {tx.quantity ?? '?'}
            {contract ? ` ${contract}` : ''} @ {fmtPrice(tx.unitPrice ?? 0)}
          </div>
        )
      })}
      {fills.length > 6 && (
        <div className="text-zinc-500 mt-0.5">+{fills.length - 6} more</div>
      )}
    </div>
  )
}
